/**
 * User menu component - dropdown menu for user actions
 */

import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { UserProfile } from './UserProfile';
import { ThemeSwitcher } from './ThemeSwitcher';

interface UserMenuProps {
  onNavigateToAdmin: () => void;
}

export function UserMenu({ onNavigateToAdmin }: UserMenuProps) {
  const { user, logout } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
  };

  const handleThemeClick = () => {
    setShowThemeMenu(true);
  };

  const handleBackToMenu = () => {
    setShowThemeMenu(false);
  };

  const handleTriggerClick = () => {
    if (!showUserMenu && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setShowUserMenu(!showUserMenu);
    setShowThemeMenu(false);
  };

  // Close menu when clicking/touching outside
  useEffect(() => {
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setShowUserMenu(false);
        setShowThemeMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showUserMenu]);

  const dropdownStyle: React.CSSProperties = {
    position: 'fixed',
    top: dropdownPos.top,
    right: dropdownPos.right,
    zIndex: 1000,
  };

  return (
    <>
      <div className="user-menu-section" ref={triggerRef}>
        <div
          className="user-menu-trigger"
          onClick={handleTriggerClick}
        >
          <span className="username">{user?.display_name}</span>
          <span className="menu-arrow">{showUserMenu ? '▲' : '▼'}</span>
        </div>
      </div>

      {showUserMenu && !showThemeMenu && ReactDOM.createPortal(
        <div className="user-dropdown" style={dropdownStyle} ref={dropdownRef}>
          <div
            className="dropdown-item"
            onClick={handleThemeClick}
          >
            THEME
          </div>
          <div
            className="dropdown-item"
            onClick={() => {
              setShowProfile(true);
              setShowUserMenu(false);
            }}
          >
            PROFILE
          </div>
          {user?.roles.includes('admin') && (
            <div
              className="dropdown-item"
              onClick={() => {
                onNavigateToAdmin();
                setShowUserMenu(false);
              }}
            >
              ADMIN
            </div>
          )}
          <div className="dropdown-item" onClick={handleLogout}>
            LOGOUT
          </div>
        </div>,
        document.body
      )}

      {showUserMenu && showThemeMenu && ReactDOM.createPortal(
        <div className="user-dropdown theme-dropdown" style={dropdownStyle} ref={dropdownRef}>
          <div
            className="dropdown-item dropdown-back"
            onClick={handleBackToMenu}
          >
            ← BACK
          </div>
          <ThemeSwitcher
            alwaysOpen={true}
          />
        </div>,
        document.body
      )}

      {showProfile && ReactDOM.createPortal(
        <UserProfile onClose={() => setShowProfile(false)} />,
        document.body
      )}
    </>
  );
}
